import { createRouter as createRouterBase } from '@updater-core/lib/router';
import { ENVIRONMENT } from '@updater-core/lib/environment';
import { track, TRACK, VIEW } from '@updater-core/lib/tracker';
import { isFeatureEnabled } from '@updater-core/lib/optimizely';
import { createLogger } from '@updater-core/lib/logger';
import { startSpan, tagError } from '@updater-core/lib/tracing';
import {
  isAMoverAppRoute,
  directChangeToMoverApp,
} from './handle-mover-app-routes';
import { getRouteConfigForPath, fadeTransition, enqueue } from './utils';
import { connectChildAwareHistory } from './connect-child-aware-history';

const { log } = createLogger(
  ENVIRONMENT.debug?.appShell,
  '[DEBUG][APP-SHELL]',
  'color: #286d5b'
);

async function isForcedIntoProductIntro() {
  return isFeatureEnabled('Product_Intro_Force').catch(() => false);
}

const routes = [
  {
    path: '/(sign-up|invite).*',
    unauthenticatedPaths: ['.*'],
    analytics: {
      name: 'signup',
    },
    load: ({ parentSpan } = {}) => {
      const span = startSpan('router.signup.load', { childOf: parentSpan });
      return import(
        /* webpackChunkName: "signup" */ '../../apps/sign-up/index.js'
      )
        .then((response) => {
          span.finish();
          return response;
        })
        .catch((e) => {
          tagError(span, e);
          span.finish();
          throw e;
        });
    },
    resolvePath: (targetPath, { interactors }) => {
      const authenticated = interactors.session.selectors.isAuthenticated(
        interactors.session.getState()
      );
      if (authenticated) {
        return '/';
      }
      return targetPath;
    },
  },
  {
    path: `/deals.*`,
    analytics: {
      name: 'deals',
    },
    handleChildRouting: true,
    load: () =>
      import(/* webpackChunkName: "deals" */ '../../apps/deals/index.js'),
  },
  {
    path: '/maintenance',
    analytics: {
      name: 'maintenance',
    },
    unauthenticatedPaths: ['.*'],
    load: () =>
      import(
        /* webpackChunkName: "maintenance" */ '../../apps/maintenance/index.js'
      ),
    resolvePath: (targetPath, { interactors: { maintenance } = {} }) =>
      new Promise((resolve) => {
        maintenance.connect((state) => {
          if (maintenance.selectors.checked(state)) {
            if (
              maintenance.selectors.underMaintenance(state) ||
              targetPath.includes('show_maintenance=')
            ) {
              resolve(targetPath);
            }

            resolve('/');
          }
        });
      }),
  },
  {
    path: '/sign-out',
    resolvePath: (targetPath, { interactors }) =>
      interactors.journey.actions.fetchJourney().then((state) => {
        if (interactors.journey.selectors.signoutDisabled(state)) {
          return '/';
        }
        return interactors.session.actions
          .clearSession(interactors.session.getState())
          .then(() => {
            track({
              verb: TRACK,
              object: 'signout',
            });
            return '/sign-in';
          });
      }),
  },
  {
    path: '^/sign-in',
    analytics: {
      name: 'sign_in',
    },
    unauthenticatedPaths: ['.*'],
    load: ({ parentSpan } = {}) => {
      const span = startSpan('router.mover.load', { childOf: parentSpan });
      return import(
        /* webpackChunkName: "sign-in" */ '../../apps/sign-in/index.js'
      )
        .then((response) => {
          span.finish();
          return response;
        })
        .catch((e) => {
          tagError(span, e);
          span.finish();
          throw e;
        });
    },
    resolvePath: (targetPath, { interactors }) => {
      const authenticated = interactors.session.selectors.isAuthenticated(
        interactors.session.getState()
      );
      if (authenticated) {
        return '/';
      }
      return targetPath;
    },
  },
  {
    path: '/signed-up',
    resolvePath: async (targetPath) => {
      if (await isForcedIntoProductIntro()) {
        return '/product-intro';
      }
      track({
        verb: TRACK,
        object: 'signed_up_transition',
      });

      // Let the angular app handle the sign up redirects
      if (targetPath.includes('code=')) {
        track({
          object: 'sign-up-redirect',
          verb: TRACK,
          details: {
            targetPath,
          },
        });

        return `/product-intro${targetPath.substr(targetPath.indexOf('?'))}`;
      }

      return '/product-intro';
    },
  },
  {
    path: '/covid-19',
    analytics: {
      name: 'covid',
    },
    load: () =>
      import(/* webpackChunkName: "covid" */ '../../apps/covid/index.js'),
  },
  // {
  //   path: '/personalize-move.*',
  //   analytics: {
  //     name: 'personalize_move',
  //   },
  //   handleChildRouting: true,
  //   load: () =>
  //     import(/* webpackChunkName: "onboarding-wizard" */ '../../apps/onboarding-wizard/index.js'),
  // },
  {
    path: '/subscription-center',
    analytics: {
      name: 'subscription_center',
    },
    unauthenticatedPaths: ['.*'],
    handleChildRouting: true,
    load: () =>
      import(
        /* webpackChunkName: "subscription-center" */ '../../apps/subscription-center/index.js'
      ),
    resolvePath: (targetPath) => targetPath,
  },
  {
    path: '/claim-redeem-offer',
    unauthenticatedPaths: ['.*'],
    load: () =>
      import(
        /* webpackChunkName: "claim-redeem-redirect" */ '../../apps/claim-redeem-redirect/index.js'
      ),
  },
  {
    path: '/promotion-interstitial',
    unauthenticatedPaths: ['.*'],
    load: () =>
      import(
        /* webpackChunkName: "promotion-interstitial" */ '../../apps/promotion-interstitial/index.js'
      ),
  },
  {
    path: '^/utilities-confirmation/.*',
    unauthenticatedPaths: ['.*'],
    load: () =>
      import(
        /* webpackChunkName: "utilities-landing-confirmation" */ '../../apps/utilities/landing/index.js'
      ),
  },
  {
    path: '/professionals/sign-in',
    unauthenticatedPaths: ['.*'],
    resolvePath: () => {
      window.location.href = `${window.location.origin}/professionals/sign-in`;
    },
  },
  {
    path: '^/utilities-dashboard.*',
    // unauthenticatedPaths: ['.*'],
    load: () =>
      import(
        /* webpackChunkName: "utilities-dashboard" */ '../../apps/utilities/dashboard/index.js'
      ),
  },
  {
    path: '/.*',
    preload: true,
    unauthenticatedPaths: [
      /\/share\/.*/,
      /forgot-password/,
      /reset-password/,
      /unsubscribe\/invite\/.*/,
    ],
    load: ({ parentSpan } = {}) => {
      const span = startSpan('router.mover.load', { childOf: parentSpan });
      return import(
        /* webpackChunkName: "mover-app", webpackPrefetch: true */ '../../apps/angularjs'
      )
        .then((response) => {
          span.finish();
          return response;
        })
        .catch((e) => {
          tagError(span, e);
          span.finish();
          throw e;
        });
    },
  },
];

function isAuthenticated(session) {
  return session && session.selectors.isAuthenticated(session.getState());
}

function isAnUnauthenticatedPath(routeConfig, targetPath) {
  return (
    Array.isArray(routeConfig.unauthenticatedPaths) &&
    routeConfig.unauthenticatedPaths.some((path) => targetPath.match(path))
  );
}

function resolvePath(props) {
  return async (targetPath) => {
    const routeConfig = getRouteConfigForPath({ path: targetPath, routes });
    const { session } = props.interactors;
    if (
      !isAuthenticated(session) &&
      !isAnUnauthenticatedPath(routeConfig, targetPath)
    ) {
      return '/sign-in';
    }

    if (targetPath === props.router.getInitialPath()) {
      props.router.clearInitialPath();
    }

    return routeConfig.resolvePath
      ? routeConfig.resolvePath(targetPath, props)
      : targetPath;
  };
}

function preloadModules() {
  // Preload in a "setTimeout" so that it doesnt interrupt main thread
  setTimeout(
    () =>
      Promise.all(
        routes
          .filter(({ preload }) => preload)
          .map((config) =>
            config.load().then((bundle) => ({ ...config, bundle }))
          )
      ).then((result) => {
        log('preloaded modules', result);
      }),
    1500
  );
}

export function createRouter({ container, interactors }) {
  /* Allow us to retrieve the interactors set at runtime. */
  // eslint-disable-next-line no-underscore-dangle
  let _interactors = interactors;
  let router;

  const props = Object.create(
    {},
    {
      container: {
        value: container,
      },
      interactors: {
        get: () => _interactors,
      },
      router: {
        get: () => router,
      },
    }
  );

  const routerBase = createRouterBase(resolvePath(props));

  const path = window.location.pathname || '';
  const queryParams = window.location.search || '';
  let initialPath = path + queryParams;

  router = {
    ...routerBase,
    setInteractors: (fn) => {
      _interactors = fn(_interactors);
    },
    getInitialPath: () => initialPath,
    clearInitialPath: () => {
      initialPath = null;
    },
    // eslint-disable-next-line @typescript-eslint/no-shadow
    paths: routes.map(({ path }) => path),
    init,
    selectors: {
      query() {
        return new URLSearchParams(window.location.search);
      },
    },
  };

  function init() {
    const childAwareHistory = connectChildAwareHistory({ router, routes });

    router.subscribe(({ newPath }) => {
      const routeConfig = getRouteConfigForPath({ path: newPath, routes });
      const pageName = routeConfig?.analytics?.name;

      if (pageName) {
        track({
          object: pageName,
          verb: VIEW,
        });
      }
    });

    let currentElement;
    let currentModule;

    const mountRoute = enqueue(({ newPath }) => {
      const routeConfig = getRouteConfigForPath({ path: newPath, routes });
      const span = startSpan('router.pathchange');
      span.setTag('pathname', newPath);

      console.log({
        newPath,
        routeConfig,
        routes,
      });

      return routeConfig.load({ parentSpan: span }).then((nextModule) => {
        log('loading next module', { newPath, nextModule });
        const nextElement = document.createElement('div');

        if (nextModule === currentModule) {
          log('skipping module mount');
          // Grandfather in mover app changes. We need to be able to route into
          // the mover app from the daisy chain so we handle a select few mover
          // app route transitions directly.
          if (isAMoverAppRoute(newPath)) {
            log('detected mover app route');
            span.setTag('handledBy', 'angular-app');
            directChangeToMoverApp(newPath);
          } else {
            span.setTag('handledBy', 'child');
            log('no mover-app route detected; child to handle routing', {
              newPath,
            });
          }
          span.finish();
          return Promise.resolve();
        }

        if (!nextModule.mount) {
          let unmount;
          // eslint-disable-next-line no-param-reassign
          nextModule.mount = (...args) => {
            const { destroy } = nextModule.build(...args);
            unmount = destroy;
          };

          // eslint-disable-next-line no-param-reassign
          nextModule.unmount = () => {
            if (unmount) unmount();
          };
        }

        return fadeTransition({
          container,
          interactors: {
            ..._interactors,
            router,
          },
          currentElement,
          currentModule,
          nextElement,
          nextModule,
        })
          .then(() => {
            span.finish();
            currentElement = nextElement;
            currentModule = nextModule;
          })
          .catch((e) => {
            tagError(e);
            span.finish();
            throw e;
          });
      });
    });

    router.subscribe(mountRoute);

    return childAwareHistory.init();
  }

  preloadModules();

  return router;
}
